import type { CSSProperties } from 'react';
import { memo } from 'react';
import { columnOf } from '../data/buckets';
import styles from './Ticks.module.css';

/* Kept in sync with .tick::after in Ticks.module.css: gap + mark height. */
export const TICK_BAND = 8;

const utcYear = (t: number) => new Date(t * 1000).getUTCFullYear();

const startOfYear = (year: number) => Date.UTC(year, 0, 1) / 1000;

const yearsBetween = (start: number, end: number) => {
  const years: number[] = [];
  for (let year = utcYear(start); year <= utcYear(end); year++) {
    years.push(year);
  }
  return years;
};

const yearTicks = (start: number, end: number, n: number) =>
  yearsBetween(start, end)
    .map(startOfYear)
    .filter((t) => t >= start && t <= end)
    .map((t) => ({ t, column: columnOf(t, { start, end }, n) }));

const WEEK = 7 * 86400;

const startOfWeek = (t: number) => {
  const d = new Date(t * 1000);
  const isoDay = (d.getUTCDay() + 6) % 7;
  return (
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - isoDay) /
    1000
  );
};

const weekTicks = (start: number, end: number, n: number) => {
  const ticks: { t: number; column: number }[] = [];
  for (let t = startOfWeek(start); t <= end; t += WEEK) {
    if (t >= start) ticks.push({ t, column: columnOf(t, { start, end }, n) });
  }
  return ticks;
};

const startOfMonth = (t: number) => {
  const d = new Date(t * 1000);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) / 1000;
};

const nextMonth = (t: number) => {
  const d = new Date(t * 1000);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1) / 1000;
};

const monthTicks = (start: number, end: number, n: number) => {
  const ticks: { t: number; column: number }[] = [];
  for (let t = startOfMonth(start); t <= end; t = nextMonth(t)) {
    if (t >= start) ticks.push({ t, column: columnOf(t, { start, end }, n) });
  }
  return ticks;
};

export type TickUnit = 'year' | 'month' | 'week';

const TICKS: Record<
  TickUnit,
  (start: number, end: number, n: number) => { t: number; column: number }[]
> = { year: yearTicks, month: monthTicks, week: weekTicks };

export const Ticks = memo(
  ({
    start,
    end,
    n,
    unit = 'year',
  }: {
    start: number;
    end: number;
    n: number;
    unit?: TickUnit;
  }) => (
    <div className={styles.tickRow}>
      {n > 1 &&
        TICKS[unit](start, end, n).map(({ t, column }) => (
          <div
            className={styles.tick}
            key={t}
            style={
              {
                right: `${(n - 1 - column) * 2}px`,
                '--col': column,
              } as CSSProperties
            }
          />
        ))}
    </div>
  )
);
Ticks.displayName = 'Ticks';
